import React, { Dispatch, SetStateAction } from 'react'
import { useIntl } from 'react-intl'

import { noop } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { BodyTextSize, TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { mixpanelEvents } from '../../../../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../../../../mixpanel/mixpanelTracking'
import { LWUrl, providerInitialDirectBookingLink } from '../../../../../common/constants/appConstants'
import { useIsEngageDAProvider } from '../../../../../hooks/useIsEngageDAProvider'

const EndEpisodeContainer = styled.View<{ theme: ThemeType; hasExtraPadding: boolean }>(
  ({ theme: { spacing }, hasExtraPadding }) => ({
    padding: `0 ${hasExtraPadding ? spacing['8px'] : 0}`,
    width: '100%',
  }),
)

const StyledBodyText = styled(BodyText)(({ theme: { spacing } }) => ({
  marginTop: spacing['2px'],
}))

export const useActionsMenu = (
  goToNewNote: () => void,
  canReferCare: boolean,
  userId: string,
  setToastText: Dispatch<SetStateAction<string | undefined>>,
  isEndEpisodeButtonVisible: boolean,
  endEpisode: () => void,
  canEndEpisode: boolean,
  clientDetails?: {
    employer?: string
    id?: string
  },
  onExportClientRecords?: () => void,
) => {
  const { formatMessage } = useIntl()
  const { colors } = useTheme()
  const { isDAProviderMigrated, isClientHeaderRedesignEnabled } = useFlags()
  const isEngageDAProvider = useIsEngageDAProvider()

  const closeAllSessionsText = isDAProviderMigrated
    ? formatMessage({
        defaultMessage: 'Submit session details and invoices before discharging client.',
        description: 'Ensure all sessions are closed before attemping to discharge client',
      })
    : formatMessage({
        defaultMessage: 'Close all sessions before ending episode.',
        description: 'Ensure all sessions are closed before attemping to end episode',
      })

  return [
    ...(!isEngageDAProvider
      ? [
          ...(!isClientHeaderRedesignEnabled
            ? [
                {
                  text: 'Care referral form',
                  customTextComponent: (
                    <Title
                      text={formatMessage({
                        defaultMessage: 'Care referral form',
                        description: 'Label to go to the care referral form',
                      })}
                      size={TitleSize.SMALL}
                    />
                  ),
                  id: 'referralForm',
                  testId: 'referralForm',
                  onPressItem: () => (goToNewNote ?? noop)(),
                  disabled: !canReferCare,
                },
              ]
            : []),
          {
            text: 'Share provider profile link',
            customTextComponent: (
              <Title
                text={formatMessage({
                  defaultMessage: 'Share provider profile link',
                  description: 'Label to copy provider profile link',
                })}
                size={TitleSize.SMALL}
              />
            ),
            id: 'shareProfile',
            testId: 'shareProfile',
            onPressItem: () => {
              navigator.clipboard.writeText(
                providerInitialDirectBookingLink({
                  employer: clientDetails?.employer || '',
                  providerId: userId,
                }),
              )
              setToastText('Link successfully copied')

              // Clear toast after 3 seconds
              setTimeout(() => {
                setToastText(undefined)
              }, 3000)
            },
          },
        ]
      : []),
    ...(clientDetails?.employer
      ? [
          {
            text: 'Share release of information link',
            customTextComponent: (
              <Title
                text={formatMessage({
                  defaultMessage: 'Share release of information link',
                  description: 'Label to copy release of information form link',
                })}
                size={TitleSize.SMALL}
              />
            ),
            id: 'shareROILink',
            testId: 'shareROILink',
            onPressItem: () => {
              navigator.clipboard.writeText(`${LWUrl(clientDetails!.employer!)}/secure/index/clinical-data-sharing`)
              setToastText(
                formatMessage({
                  defaultMessage: 'Link successfully copied',
                  description: 'Toast message shown when link is copied to clipboard',
                }),
              )

              // Clear toast after 3 seconds
              setTimeout(() => {
                setToastText(undefined)
              }, 3000)
            },
          },
        ]
      : []),
    ...(isClientHeaderRedesignEnabled && onExportClientRecords
      ? [
          {
            text: 'Export client records',
            customTextComponent: (
              <Title
                text={formatMessage({
                  defaultMessage: 'Export client records',
                  description: 'Menu item text for exporting client records',
                })}
                size={TitleSize.SMALL}
              />
            ),
            id: 'exportClientRecords',
            testId: 'exportClientRecords',
            onPressItem: onExportClientRecords,
          },
        ]
      : []),
    ...(isEndEpisodeButtonVisible
      ? [
          {
            text: isDAProviderMigrated ? 'Discharge client' : 'End episode',
            customTextComponent: (
              <EndEpisodeContainer hasExtraPadding={!canEndEpisode}>
                <Title
                  text={
                    isDAProviderMigrated
                      ? formatMessage({
                          defaultMessage: 'Discharge client',
                          description: 'Button to end client episode',
                        })
                      : formatMessage({
                          defaultMessage: 'End episode',
                          description: 'Button to end client episode',
                        })
                  }
                  size={TitleSize.SMALL}
                  color={colors.textWarning}
                />
                {!canEndEpisode && (
                  <StyledBodyText
                    testID={tID('ClientDetails-endEpisodeWarning')}
                    size={BodyTextSize.SMALL}
                    text={closeAllSessionsText}
                    color={colors.textSecondary}
                  />
                )}
              </EndEpisodeContainer>
            ),
            id: 'endEpisode',
            testId: 'endEpisode',
            onPressItem: () => {
              if (isDAProviderMigrated) {
                track({ event: mixpanelEvents.DISCHARGE_CLIENT, properties: { clientLyraId: clientDetails?.id } })
              } else {
                track({ event: mixpanelEvents.END_EPISODE, properties: { clientLyraId: clientDetails?.id } })
              }
              ;(endEpisode ?? noop)()
            },
            disabled: !canEndEpisode,
          },
        ]
      : []),
  ]
}
